import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Button from "app/popleads/components/ui-kit/Button";
import Avatar from "app/popleads/components/ui-kit/Avatar";
import TextAreaInput from "app/popleads/components/ui-kit/TextAreaInput";
import { BodyStrong, Caption, FieldLabel } from "app/popleads/components/ui-kit/Text";
import ChatItem from "./AssistantChatItem";
import ChatSkeleton from "./AssistantChatSkeleton";
import AttachmentChip from "./AttachmentChip";
import { usageLabel, usageShare, usageTone } from "./assistantUsage";
import useDisclosure from "app/popleads/hooks/useDisclosure";
import FeatherIcon from "feather-icons-react";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { ASSISTANT_ARCHIVE, ASSISTANT_ORB, ASSISTANT_SPARK } from "./assistantIcons";
import useAssistantThread from "./useAssistantThread";
import { moreCommands } from "./assistantCommands";

import styles from "./AssistantDrawer.module.scss";

/* Two instances can mount on one screen — the subheader renders one and a page's action bar can
   render another. The palette is portalled, so this id is how the shortcut tells whether one is
   already open and refuses to open a second. */
const PALETTE_ID = "assistant-palette";

/**
 * A command palette rather than a chat window. The assistant's new job is to read live data and
 * report on it, and the thing a user reaches for to run something over their data is a palette:
 * one keystroke from anywhere, a list of what it can do, and the answer in the same frame.
 *
 * The composer is in the footer and the list above it does **not** filter as you type. A palette
 * that rewrites itself on every keystroke makes the free-form case feel like a failed search —
 * the list is a set of shortcuts, and typing is the other way in, not a query against them.
 *
 * Portalled to `document.body`: the trigger renders inside the subheader, which clips it to a
 * 40px box on desktop, and a full-screen overlay cannot live inside that.
 *
 * This is the CRM's shell. The conversation itself is `useAssistantThread`, shared with the
 * extension's panel — see `modules/extension/components/assistant/ExtensionAssistantPage`.
 */
/* The width at which the conversations column sits beside the thread. Under it the column takes the
   whole window (see the phone rules in the stylesheet), so picking a conversation has to close it
   or the thread is never seen. Read on every render rather than kept in state: the value only has
   to be right at the moment a conversation is picked, and a pick is itself a render. */
const WIDE_WINDOW = "(min-width: 768px)";

/* The composer's tallest, matching the stylesheet's `max-height`; past it the text scrolls. */
const COMPOSER_MAX_HEIGHT = 116;
const isWideWindow = () => typeof window !== "undefined" && window.matchMedia(WIDE_WINDOW).matches;

/* How "Show less" times the rows leaving, matching `.suggestionListOut` in the stylesheet: each row
   this much after the one below it, each fade this long. */
const ROW_OUT_STAGGER_MS = 8;
const ROW_OUT_MS = 140;

/* ChatGPT's own shortcut for a new chat, shown beside the row the way it shows it. */
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
const NEW_CHAT_KEYS = IS_MAC ? "⇧⌘O" : "Ctrl+Shift+O";

/* The empty window opens on the user's name rather than a description of the assistant — the
   suggestions under the composer already say what it is for. */
const greetingFor = (name) => {
    const hour = new Date().getHours();
    const part = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
    const firstName = String(name || "")
        .trim()
        .split(/\s+/)[0];
    return firstName ? `Good ${part}, ${firstName}` : `Good ${part}`;
};

/* Searched in the browser: the archive already holds every saved conversation's messages, and the
   server caps it at 30, so there is nothing to ask the backend for. The title, then what was said. */
const conversationMatches = (conversation, term) => {
    if (
        String(conversation.title || "")
            .toLowerCase()
            .includes(term)
    )
        return true;
    const messages = Array.isArray(conversation.messages) ? conversation.messages : [];
    return messages.some((message) =>
        String(message.content || "")
            .toLowerCase()
            .includes(term)
    );
};

const ChatGptDrawer = ({ className, style }) => {
    const {
        assistantName,
        user,
        messages,
        isLoading,
        failedQuestion,
        isBrowsing,
        retry,
        messageInput,
        setMessageInput,
        canSend,
        sendMessage,
        attachments,
        addAttachments,
        removeAttachment,
        commands,
        showCommands,
        runCommand,
        conversationGroups,
        conversationId,
        conversationTitle,
        isArchiveOpen,
        setIsArchiveOpen,
        refreshConversations,
        openConversation,
        removeConversation,
        startNewChat,
        approvingId,
        respondToApproval,
        blockApproval,
        failApproval,
        appendLocalTurn,
        usage,
        /* Open by default: past conversations are a standing part of this dialog, the way ChatGPT's
           sidebar is, not something to go and find. Its own toggle collapses it to the rail. */
    } = useAssistantThread({ initialArchiveOpen: true, keepArchiveOpen: isWideWindow() });

    const { isOpen, onOpen, onClose } = useDisclosure();

    /* Search over the conversations column, and which row's "More" menu is open. */
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [menuFor, setMenuFor] = useState(null);
    /* Whether "More" has turned the five into the list of every suggestion, and whether the five are
       back from it — only then do they fade in; on opening they simply appear with the window. */
    const [showMore, setShowMore] = useState(false);
    const [isBackFromMore, setIsBackFromMore] = useState(false);

    /* The empty window's block — greeting, composer, suggestions — and where it stood before "More"
       or "Show less" changed its height. It is centred, so a change in height moves it, and that
       move is animated from the old place to the new one (see the layout effect below). */
    const emptyBlockRef = useRef(null);
    const emptyBlockTopRef = useRef(null);

    const toggleMore = (next) => {
        if (emptyBlockRef.current) emptyBlockTopRef.current = emptyBlockRef.current.getBoundingClientRect().top;
        setShowMore(next);
        setIsBackFromMore(!next);
    };

    /* "Show less" is "More" played backwards: the rows leave last first, and only then does the list
       give way to the five and the block slide down. The wait is the stylesheet's — the last row's
       delay plus its fade — so the two have to change together. */
    const [isClosingMore, setIsClosingMore] = useState(false);
    const closeMoreTimerRef = useRef(null);
    const suggestionCount = commands.length + moreCommands.length + 1;

    const closeMore = () => {
        if (isClosingMore) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            toggleMore(false);
            return;
        }
        setIsClosingMore(true);
        closeMoreTimerRef.current = setTimeout(() => {
            closeMoreTimerRef.current = null;
            setIsClosingMore(false);
            toggleMore(false);
        }, (suggestionCount - 1) * ROW_OUT_STAGGER_MS + ROW_OUT_MS);
    };

    useEffect(() => () => clearTimeout(closeMoreTimerRef.current), []);

    /* Read by the shortcut listener, which is bound once per open rather than on every render. */
    const startNewChatRef = useRef(startNewChat);
    startNewChatRef.current = startNewChat;

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const searchRef = useRef(null);
    const fileInputRef = useRef(null);

    /* A counter, not a boolean: the palette has children, and the browser fires a `dragleave` for
       every one of them as the cursor crosses into a child element, each paired with a `dragenter`
       on the way in. Counting nets those out; a boolean would flicker the overlay off between
       children on the way across the palette. */
    const dragCounterRef = useRef(0);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleDragEnter = (e) => {
        e.preventDefault();
        if (!e.dataTransfer.types.includes("Files")) return;
        dragCounterRef.current += 1;
        setIsDragActive(true);
    };

    const handleDragOver = (e) => {
        /* Without this, the browser's default is to reject the drop entirely — dragging over an
           element that never called `preventDefault` on `dragover` shows the "no drop" cursor. */
        e.preventDefault();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDragActive(false);
        addAttachments(e.dataTransfer.files);
    };

    /* Read the archive when the palette opens rather than on mount: it is browser storage, and the
       list can have been added to by another tab since this component mounted. */
    useEffect(() => {
        if (isOpen) refreshConversations();
    }, [isOpen, refreshConversations]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [isLoading, messages]);

    /* Grow with the text instead of scrolling inside a fixed box, and scroll only once the text
       outgrows the cap. A 14px line at 1.45 is a fraction of a pixel taller than the whole-pixel
       height `scrollHeight` reports, and with scrolling always on that sliver drew a scrollbar in
       an empty box. */
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
        el.style.overflowY = el.scrollHeight > COMPOSER_MAX_HEIGHT ? "auto" : "hidden";
    }, [messageInput, isOpen, isBrowsing]);

    /* Again when the composer moves — from the middle to the foot on the first question, and back on
       a new chat — since moving it mounts a fresh textarea that has not got the focus; and once an
       answer is in, since the textarea is disabled while it loads and a disabled field drops it. */
    useEffect(() => {
        if (isOpen && !isLoading && inputRef.current) inputRef.current.focus();
    }, [isOpen, isBrowsing, isLoading]);

    /* Cmd/Ctrl+K opens it from anywhere, Cmd/Ctrl+Shift+O starts a new chat inside it, Escape
       closes it. */
    useEffect(() => {
        const onKeyDown = (e) => {
            if (isOpen && (e.metaKey || e.ctrlKey) && e.shiftKey && e.key?.toLowerCase() === "o") {
                e.preventDefault();
                startNewChatRef.current();
                return;
            }
            if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === "k") {
                e.preventDefault();
                if (isOpen) onClose();
                else if (!document.getElementById(PALETTE_ID)) onOpen();
                return;
            }
            /**
             * Escape closes the palette. It used to collapse the archive first and take a second
             * press to leave — reasonable while the archive was a thing you opened, wrong now that
             * it is open by default: every Escape would have gone to collapsing a sidebar the user
             * never asked to open, and leaving would have needed two presses every time.
             */
            /* An Escape something inside has already used — leaving the search, closing a row's
               menu — is not also a request to close the window. */
            if (e.key === "Escape" && isOpen && !e.defaultPrevented) onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onOpen, onClose]);

    /* A row's menu closes on a click anywhere but the menu and its own button, and on Escape. Tested
       by target rather than stopped in React: React 16 listens on `document` too, so stopping the
       event there would not keep it from this listener. The button is spared so its click can close
       the menu itself — closed here first, the click would only open it again. */
    useEffect(() => {
        if (menuFor === null) return undefined;
        const close = () => setMenuFor(null);
        const onMouseDown = (e) => {
            if (e.target.closest(`.${styles.archiveMenu}, .${styles.archiveMore}`)) return;
            close();
        };
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            close();
        };
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKey, true);
        };
    }, [menuFor]);

    /* Focus the search field once it is there. Not `autoFocus`: opened from the rail, the column is
       still `visibility: hidden` on the frame the field mounts — the transition starts there — and a
       hidden field refuses the focus. */
    useEffect(() => {
        if (!isSearching || !isArchiveOpen) return undefined;
        const timer = setTimeout(() => searchRef.current?.focus(), 50);
        return () => clearTimeout(timer);
    }, [isSearching, isArchiveOpen]);

    /**
     * The block slides to where the new height puts it instead of jumping there. Measured after
     * React has laid the change out and before the browser paints it: the block is pushed back by
     * the distance it moved, then let go with a transition, so the first frame painted is the old
     * position. A layout effect, not an ordinary one — after a paint the jump would already show.
     */
    useLayoutEffect(() => {
        const el = emptyBlockRef.current;
        const from = emptyBlockTopRef.current;
        emptyBlockTopRef.current = null;
        if (!el || from === null) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        const distance = from - el.getBoundingClientRect().top;
        if (Math.abs(distance) < 1) return;

        el.style.transition = "none";
        el.style.transform = `translateY(${distance}px)`;
        el.getBoundingClientRect(); // commit the starting point before the transition is set
        el.style.transition = "transform 0.36s cubic-bezier(0.16, 1, 0.3, 1)";
        el.style.transform = "";
    }, [showMore]);

    /* A new chat opens on the five again, the way it first did. */
    useEffect(() => {
        if (!isBrowsing) return;
        clearTimeout(closeMoreTimerRef.current);
        setIsClosingMore(false);
        setShowMore(false);
        setIsBackFromMore(false);
    }, [isBrowsing]);

    /* Each opening starts from the plain list, not from a search left behind last time. */
    useEffect(() => {
        if (isOpen) return;
        setIsSearching(false);
        setSearchQuery("");
        setMenuFor(null);
    }, [isOpen]);

    /* The header names the thread, the way CAIN's panels carry a title: a saved conversation by its
       stored title, an unsaved one by its first question, and an empty one as a new chat. */
    const hasQuestion = messages.some((message) => message.role === "user");
    const savedConversation = conversationGroups
        .flatMap((group) => group.items)
        .find((conversation) => conversation.id === conversationId);
    const threadTitle = hasQuestion
        ? (savedConversation && savedConversation.title) || conversationTitle(messages)
        : "New chat";

    /* The greeting is the thread's first message — the hook keeps it there and leaves it out of what
       it sends — but ChatGPT opens on the composer, not on a line of the assistant introducing
       itself, so this window does not draw it. The same test the hook's request uses. */
    const visibleMessages = messages.filter((message, index) => !(index === 0 && message.role === "assistant"));

    const searchTerm = searchQuery.trim().toLowerCase();
    const shownGroups = searchTerm
        ? conversationGroups
              .map((group) => ({ ...group, items: group.items.filter((c) => conversationMatches(c, searchTerm)) }))
              .filter((group) => group.items.length > 0)
        : conversationGroups;

    const beginSearch = () => {
        setIsArchiveOpen(true);
        setIsSearching(true);
    };

    const endSearch = () => {
        setIsSearching(false);
        setSearchQuery("");
    };

    const pickConversation = (conversation) => {
        setMenuFor(null);
        openConversation(conversation);
    };

    /* The assistant's name and mark, at the top of the conversations column — the way ChatGPT carries
       its own above "New chat". */
    const identity = (
        <>
            <Avatar src={toAbsoluteUrl(ASSISTANT_ORB)} size={28} radius={14} />
            <BodyStrong className={styles.identityName}>{assistantName}</BodyStrong>
        </>
    );

    /* The column is there by default, so this collapses it rather than revealing it — and the name
       has to say which, because the glyph cannot. */
    const archiveToggle = (
        <Button
            label={isArchiveOpen ? "Hide past conversations" : "Show past conversations"}
            tooltip={isArchiveOpen ? "Close sidebar" : "Open sidebar"}
            iconPath={ASSISTANT_ARCHIVE}
            variant="neutral"
            buttonStyle="borderless"
            size="small"
            className={styles.archiveToggle}
            onClick={() => setIsArchiveOpen((value) => !value)}
        />
    );

    /* How much of today's limit is used, at the foot of the left column: a bar while the column is
       open, a ring on the rail once it is closed — the way Claude Code shows it. A share, not tokens:
       the limit counts tokens, but a question costs a varying number of them, so a share is the one
       figure that stays true. Nothing at all when there is no limit, or before the backend has said. */
    const share = usageShare(usage);
    const tone = usageTone(share);
    const toneClass = tone === "full" ? styles.usageFull : tone === "warning" ? styles.usageWarning : "";
    const usageBar =
        share === null ? null : (
            <div className={`${styles.usage} ${toneClass}`}>
                <span className={styles.usageText}>{usageLabel(share)}</span>
                <span
                    className={styles.usageTrack}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={share}
                    aria-label="Today's assistant usage"
                >
                    <span className={styles.usageFill} style={{ width: `${Math.max(share, 2)}%` }} />
                </span>
            </div>
        );
    const RING = 2 * Math.PI * 8;
    const usageRing =
        share === null ? null : (
            <span
                className={`${styles.usageRing} ${toneClass}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={share}
                aria-label={usageLabel(share)}
                title={usageLabel(share)}
            >
                <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
                    <circle className={styles.usageRingTrack} cx="11" cy="11" r="8" />
                    <circle
                        className={styles.usageRingFill}
                        cx="11"
                        cy="11"
                        r="8"
                        strokeDasharray={RING}
                        strokeDashoffset={RING * (1 - Math.max(share, 2) / 100)}
                    />
                </svg>
            </span>
        );

    const archive = (
        <aside
            className={`${styles.archive} ${isArchiveOpen ? styles.archiveOpen : ""}`}
            aria-hidden={!isArchiveOpen}
            aria-label="Past conversations"
        >
            <div className={styles.archiveInner}>
                <div className={styles.archiveHead}>
                    {identity}
                    {archiveToggle}
                </div>

                {/* ChatGPT's first two rows, in its order: a new chat, then a search over the old ones. */}
                <div className={styles.archiveActions}>
                    <Button
                        variant="neutral"
                        buttonStyle="borderless"
                        size="small"
                        leftIcon="edit"
                        className={styles.archiveAction}
                        onClick={startNewChat}
                        disabled={isLoading}
                    >
                        <span className={styles.archiveActionLabel}>New chat</span>
                        <kbd className={styles.archiveActionKey}>{NEW_CHAT_KEYS}</kbd>
                    </Button>

                    {isSearching ? (
                        <div className={styles.archiveSearch}>
                            <FeatherIcon icon="search" size={15} />
                            <input
                                ref={searchRef}
                                type="text"
                                value={searchQuery}
                                placeholder="Search chats"
                                aria-label="Search chats"
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    /* Escape leaves the search, not the window — the window's own
                                       Escape listener skips an event this has already handled. */
                                    if (e.key === "Escape") {
                                        e.preventDefault();
                                        endSearch();
                                    }
                                }}
                            />
                            <Button
                                label="Close search"
                                tooltip="Close search"
                                icon="x"
                                variant="neutral"
                                buttonStyle="borderless"
                                size="xsmall"
                                onClick={endSearch}
                            />
                        </div>
                    ) : (
                        <Button
                            variant="neutral"
                            buttonStyle="borderless"
                            size="small"
                            leftIcon="search"
                            className={styles.archiveAction}
                            onClick={beginSearch}
                        >
                            <span className={styles.archiveActionLabel}>Search chats</span>
                        </Button>
                    )}
                </div>

                <div className={styles.archiveList}>
                    <FieldLabel className={styles.archiveTitle}>Chats</FieldLabel>

                    {conversationGroups.length === 0 && (
                        <Caption className={styles.archiveEmpty}>
                            Nothing saved yet. Conversations appear here once the assistant has answered.
                        </Caption>
                    )}

                    {conversationGroups.length > 0 && shownGroups.length === 0 && (
                        <Caption className={styles.archiveEmpty}>No chats match “{searchQuery.trim()}”.</Caption>
                    )}

                    {shownGroups.map((group) => (
                        <div key={group.label} className={styles.archiveGroup}>
                            <Caption className={styles.archiveGroupLabel}>{group.label}</Caption>
                            {group.items.map((conversation) => (
                                <div
                                    key={conversation.id}
                                    className={`${styles.archiveRow} ${
                                        conversation.id === conversationId ? styles.archiveRowActive : ""
                                    } ${menuFor === conversation.id ? styles.archiveRowMenuOpen : ""}`}
                                >
                                    <Button
                                        variant="primary"
                                        buttonStyle="borderless"
                                        size="small"
                                        onClick={() => pickConversation(conversation)}
                                    >
                                        <span className={styles.archiveRowTitle}>
                                            {conversation.title || conversationTitle(conversation.messages)}
                                        </span>
                                    </Button>
                                    <Button
                                        label={`More options for ${conversation.title}`}
                                        tooltip="More"
                                        icon="more-horizontal"
                                        variant="neutral"
                                        buttonStyle="borderless"
                                        size="xsmall"
                                        className={styles.archiveMore}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setMenuFor((open) => (open === conversation.id ? null : conversation.id));
                                        }}
                                    />
                                    {menuFor === conversation.id && (
                                        <div className={styles.archiveMenu} role="menu">
                                            <button
                                                type="button"
                                                role="menuitem"
                                                className={styles.archiveMenuDanger}
                                                onClick={(event) => {
                                                    setMenuFor(null);
                                                    removeConversation(event, conversation);
                                                }}
                                            >
                                                <FeatherIcon icon="trash-2" size={14} />
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                {usageBar && <div className={styles.archiveFoot}>{usageBar}</div>}
            </div>
        </aside>
    );

    /* With the column closed, ChatGPT keeps a thin rail rather than nothing: the column's own toggle,
       a new chat and search are still one click away. Search opens the column, since that is where
       the results are. */
    const rail = !isArchiveOpen && (
        <nav className={styles.rail} aria-label="Assistant">
            {archiveToggle}
            <Button
                label="New chat"
                tooltip={`New chat (${NEW_CHAT_KEYS})`}
                icon="edit"
                variant="neutral"
                buttonStyle="borderless"
                size="small"
                className={styles.railButton}
                onClick={startNewChat}
                disabled={isLoading}
            />
            <Button
                label="Search chats"
                tooltip="Search chats"
                icon="search"
                variant="neutral"
                buttonStyle="borderless"
                size="small"
                className={styles.railButton}
                onClick={beginSearch}
            />
            {usageRing && <div className={styles.railFoot}>{usageRing}</div>}
        </nav>
    );

    /* One suggestion chip. The label is its own element so a long one can be cut short on a phone
       instead of running past the edge of the window. */
    const suggestionChip = (command, onClick, className = "") => (
        <Button
            key={command.label}
            variant="neutral"
            buttonStyle="option"
            size="small"
            leftIcon={command.icon}
            className={`${styles.suggestion} ${className}`}
            onClick={onClick}
        >
            <span className={styles.suggestionLabel}>{command.label}</span>
        </Button>
    );

    /* The same suggestion as a row of the expanded list: full width, left-aligned. */
    /* `--row` is its place in the list, which the stylesheet turns into a delay so the rows arrive one
       after another rather than all at once. */
    const suggestionRow = (command, index, onClick, className = "") => (
        <Button
            key={command.label}
            variant="neutral"
            buttonStyle="borderless"
            size="small"
            leftIcon={command.icon}
            className={`${styles.suggestionRow} ${className}`}
            style={{ "--row": index }}
            onClick={onClick}
        >
            <span className={styles.suggestionLabel}>{command.label}</span>
        </Button>
    );

    /* Enter sends and Shift+Enter breaks the line, and nothing else: with the suggestions now chips
       under the box rather than a list with a highlighted row, an empty Enter has nothing to run, and
       the arrow keys are left to the caret. The hook's own handler keeps the list behaviour for the
       extension, which still draws one. */
    const onComposerKeyDown = (e) => {
        if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
        e.preventDefault();
        if (canSend) sendMessage();
    };

    /* The composer is one element in two places: in the middle of an empty window, with the
       suggestions under it, and docked at the foot once a question has been asked — the way ChatGPT
       opens. One element, so there is one textarea and one set of handlers. Staged files sit inside
       the box, above the text, as ChatGPT shows them. */
    const composer = (
        <div className={styles.composer}>
            {attachments.length > 0 && (
                <div className={styles.pendingAttachments}>
                    {attachments.map((attachment) => (
                        <AttachmentChip
                            key={attachment.id}
                            name={attachment.name}
                            onRemove={() => removeAttachment(attachment.id)}
                        />
                    ))}
                </div>
            )}

            <div className={styles.composerRow}>
                <input
                    ref={fileInputRef}
                    type="file"
                    id="assistant-attach-input"
                    multiple
                    className={styles.hiddenFileInput}
                    onChange={(e) => {
                        addAttachments(e.target.files);
                        e.target.value = "";
                    }}
                />
                <Button
                    label="Add files"
                    tooltip="Add files"
                    icon="plus"
                    variant="neutral"
                    buttonStyle="borderless"
                    size="small"
                    className={styles.attachButton}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                />
                <TextAreaInput
                    ref={inputRef}
                    className={styles.composerInput}
                    value={messageInput}
                    rows={1}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={onComposerKeyDown}
                    placeholder="Ask anything about your CRM"
                    disabled={isLoading}
                />
                <Button
                    label="Send message"
                    tooltip="Send"
                    icon="arrow-up"
                    variant="primary"
                    size="small"
                    className={styles.sendButton}
                    onClick={() => sendMessage()}
                    disabled={!canSend || isLoading}
                />
            </div>
        </div>
    );

    const palette = (
        <div className={styles.overlay} onMouseDown={onClose}>
            <section
                id={PALETTE_ID}
                className={styles.palette}
                role="dialog"
                aria-label={assistantName}
                onMouseDown={(e) => e.stopPropagation()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {archive}
                {rail}

                <div className={styles.stage}>
                    <header className={styles.paletteHeader}>
                        <h2 className={styles.threadTitle}>{threadTitle}</h2>
                        <Button
                            label="Close assistant"
                            tooltip="Close (Esc)"
                            icon="x"
                            variant="neutral"
                            buttonStyle="borderless"
                            size="small"
                            onClick={onClose}
                        />
                    </header>

                    <div className={`${styles.paletteBody} ${isBrowsing ? styles.paletteBodyEmpty : ""}`}>
                        <div className={styles.paletteColumn} ref={emptyBlockRef}>
                            {/* Nothing asked yet: one block in the middle of the window — a greeting,
                                the composer, and the suggestions as chips under it. */}
                            {isBrowsing && (
                                <>
                                    <h3 className={styles.introTitle}>{greetingFor(user?.name)}</h3>
                                    <div className={styles.emptyComposer}>{composer}</div>
                                    {/* Five chips and "More" at first. "More" turns them into a
                                        two-column list of every suggestion rather than adding chips:
                                        chips of uneven width wrap into ragged rows with one left on
                                        its own, and a grid does not. "Show less" fills the last cell. */}
                                    {showCommands && !showMore && (
                                        <div
                                            className={`${styles.suggestions} ${
                                                isBackFromMore ? styles.suggestionsIn : ""
                                            }`}
                                        >
                                            {commands.map((command, index) =>
                                                suggestionChip(command, () => runCommand(index))
                                            )}
                                            {moreCommands.length > 0 &&
                                                suggestionChip(
                                                    { label: "More" },
                                                    () => toggleMore(true),
                                                    styles.suggestionMore
                                                )}
                                        </div>
                                    )}
                                    {showCommands && showMore && (
                                        <div
                                            className={`${styles.suggestionList} ${
                                                isClosingMore ? styles.suggestionListOut : ""
                                            }`}
                                            style={{ "--rows": suggestionCount }}
                                        >
                                            {commands.map((command, index) =>
                                                suggestionRow(command, index, () => runCommand(index))
                                            )}
                                            {moreCommands.map((command, index) =>
                                                suggestionRow(command, commands.length + index, () =>
                                                    sendMessage(command.label)
                                                )
                                            )}
                                            {suggestionRow(
                                                { icon: "chevron-up", label: "Show less" },
                                                commands.length + moreCommands.length,
                                                closeMore,
                                                styles.suggestionRowLess
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {!isBrowsing && (
                                <div className={styles.thread}>
                                    {visibleMessages.map((message) => (
                                        <ChatItem
                                            key={message.id}
                                            plain
                                            role={message.role}
                                            content={message.content}
                                            timestamp={message.timestamp}
                                            attachments={message.attachments}
                                            approval={message.approval}
                                            result={message.result}
                                            isApprovalBusy={approvingId === message.id}
                                            onApprovalDecide={(approve) => respondToApproval(message, approve)}
                                            onApprovalNote={appendLocalTurn}
                                            onApprovalBlock={(reason) => blockApproval(message, reason)}
                                            onApprovalFail={(reason) => failApproval(message, reason)}
                                        />
                                    ))}
                                </div>
                            )}

                            {isLoading && <ChatSkeleton plain />}

                            {failedQuestion && !isLoading && (
                                <div className={styles.failure} role="alert">
                                    <Caption className={styles.failureText}>
                                        That request did not come back. Your question is still here.
                                    </Caption>
                                    {/* `danger`, not the assistant accent: this is the one control in the
                                        palette that reports a failure, and the kit already has the scale. */}
                                    <Button
                                        variant="danger"
                                        buttonStyle="option"
                                        size="xsmall"
                                        leftIcon="refresh-cw"
                                        onClick={retry}
                                    >
                                        Try again
                                    </Button>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    {!isBrowsing && (
                        <footer className={styles.paletteFooter}>
                            <div className={styles.paletteColumn}>
                                {composer}
                                {/* Where ChatGPT says it can make mistakes. Here the answers are
                                    figures someone may forward, so it says where they come from too. */}
                                <Caption className={styles.disclaimer}>
                                    Answers come from your PopLeads records and can be wrong. Check key figures.
                                </Caption>
                            </div>
                        </footer>
                    )}
                </div>

                {isDragActive && (
                    <div className={styles.dropOverlay}>
                        <span className={styles.dropOverlayText}>Drop to attach</span>
                    </div>
                )}
            </section>
        </div>
    );

    return (
        <>
            <span className={`${styles.trigger} ${isOpen ? styles.triggerActive : ""} ${className ?? "ml-1 ml-md-4"}`}>
                <Button
                    label={`Ask ${assistantName}`}
                    tooltip={`Ask ${assistantName} — Ctrl/Cmd + K`}
                    iconPath={ASSISTANT_SPARK}
                    /* `variant` barely matters here — `.trigger button` in the stylesheet
                       overrides every colour the kit would otherwise compute from it, to the
                       same leoron accent as the rest of the assistant rather than `primary`
                       blue. `buttonStyle` still drives the shape kit renders (bordered vs
                       filled), which the CSS then recolours: tinted while closed, matching
                       `This Year`/`Open filters`' own weight in the row, solid once open, so
                       opening the palette still reads as a state change. */
                    variant="primary"
                    buttonStyle={isOpen ? "default" : "selectedOption"}
                    size="medium"
                    style={style}
                    onClick={isOpen ? onClose : onOpen}
                />
            </span>

            {isOpen && ReactDOM.createPortal(palette, document.body)}
        </>
    );
};

export default ChatGptDrawer;
